<template>
  <div class="w-full px-4">
    <div class="grid grid-cols-1 gap-4">
      <div>

        <div v-if="flash && flash.detected">
          <p class="text-lg font-semibold mb-2">
            Detected Flash: {{ detectedLabel }}
            <span v-if="capacitySource" class="text-sm opacity-70 font-normal">(size via {{ capacitySource }})</span>
            <button class="bi bi-arrow-clockwise ml-2" @click="discover" title="Re-discover"></button>
            <button class="ml-3 underline text-sm font-normal" @click="showManualSize">Override size</button>
          </p>

          <div v-if="!hasCapacity" class="bg-red bg-opacity-20 rounded-md p-4 mb-4">
            <p class="mb-2">
              The chip responded, but we could not determine its size: SFDP was not present and RDID's capacity byte was not in a recognised range.
              Please specify the flash size manually to proceed.
            </p>
            <VButton variant="dark" @click="showManualSize">SET MANUAL SIZE</VButton>
          </div>

          <FlashActions v-if="hasCapacity" :flash="flash" v-model="this.flashData" />
        </div>
      </div>
      <div>
        <FileInfo v-if="flash && flash.detected && hasCapacity" :flash="this.flash" v-model="this.flashData" />
      </div>
      <div>
        <FlashInfo v-if="flash && flash.detected" :flash="this.flash" />
      </div>
    </div>

    <div v-if="!flash || !flash.detected" class="mt-4">
      <div class="">
        <p class="mb-4 italic">
          We use SFDP first, and fall back to RDID (Command 9F) to detect the connected chip.
          Most modern flash-chips support at least one of these. If neither works you can still specify the flash size manually and operate on the chip with conservative defaults (3-byte addressing, or dedicated 4-byte opcodes above 16 MiB, 256-byte page, 4KB erase).
        </p>
        <div class="flex justify-center mt-10 mb-8">
          <VButton @click="discover">DISCOVER</VButton>
          <VButton @click="showManualSize" variant="dark" group="none" class="ml-4">MANUAL SIZE</VButton>
        </div>
      </div>
    </div>

    <Modal ref="manualSizeModal" title="Specify flash size">
      <template #body>
        <p class="mb-3 text-sm">
          Select a common size or enter a custom byte count. This overrides any detected size and enables read/erase/write with default commands.
        </p>
        <label class="block mb-1 text-sm">Size</label>
        <select v-model="manualSizeSelection" class="text-black rounded px-2 py-1 w-full mb-3">
          <option v-for="s in commonSizes" :key="s.value" :value="s.value">{{ s.label }}</option>
          <option value="custom">Custom…</option>
        </select>
        <div v-if="manualSizeSelection === 'custom'">
          <label class="block mb-1 text-sm">Custom size (bytes)</label>
          <input v-model.number="customSize" type="number" min="1" class="text-black rounded px-2 py-1 w-full" />
        </div>
      </template>
      <template #footer>
        <VButton variant="dark" @click="closeManualSize" class="mr-2">CANCEL</VButton>
        <VButton variant="green" @click="applyManualSize">APPLY</VButton>
      </template>
    </Modal>

    <!-- Toast -->
    <div class="fixed bottom-4 right-4">
      <div id="toast" class="bg-dark text-white rounded-lg shadow-lg hidden" role="alert">
        <div class="flex items-center justify-between px-4 py-2 border-b border-gray-700">
          <span class="font-semibold">Notification</span>
          <button type="button" class="text-gray-400 hover:text-gray-200" data-bs-dismiss="toast">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div class="px-4 py-3">
          {{ toastMessage }}
        </div>
      </div>
    </div>

    <div class="flex-1 lg:ml-5">
      <!-- Progress Modal - Hidden by default -->
      <div class="modal duration-100 fade fixed inset-0 bg-blacker bg-opacity-70 bg-blurry hidden" id="flashModal"
        tabindex="-1" aria-labelledby="flashModalLabel">
        <div class="flex items-center justify-center h-full">
          <div
            class="modal-dialog bg-dark rounded-lg inline-block shadow-wide shadow-lg max-w-md w-full md:w-1/2">
            <div class="modal-content">
              <div
                class="modal-header border-b border-grey border-opacity-25 px-6 py-4 flex justify-between items-center">
                <h5 class="modal-title text-lg font-semibold" id="flashModalLabel">SPI bus is busy</h5>
              </div>
              <div class="modal-body px-6 py-4">
                The SPI bus is currently busy and driven by another chip. Waiting for it to be idle before trying to
                communicate with the flash.
              </div>
              <div class="modal-footer flex justify-end px-4 mb-4 mt-2">
                <VButton variant="dark" @testclick="cancelOperation">CANCEL</VButton>
                <!-- <button v-if="showDownloadButton" type="button" @click="cancelAndDownloadPartialDump" class="text-sm rounded-md focus:outline-none whitespace-nowrap bg-opacity-100 dark:bg-red dark:focus:ring-red bg-red focus:ring-red focus:ring hover:bg-opacity-70 hover:dark:bg-opacity-70 text-white">
                                    <span><div class="py-2 px-5 whitespace-nowrap">CANCEL & DOWNLOAD PARTIAL</div></span>
                                </button> -->
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { Flashdumper } from '../flash/flashdumper';
import SFDP from './SFDP.vue';
import FlashActions from './FlashActions.vue';
import * as bootstrap from "bootstrap";
import { formatMemorySize, buf2hex } from '../flash/util';
import VButton from './VButton.vue';
import findVendor from '../flash/jedec_identification_codes';
import FlashInfo from './FlashInfo.vue';
import FileInfo from './FileInfo.vue';
import Modal from './Modal.vue';


export default {
  name: 'Flasher',
  components: {
    SFDP,
    FileInfo,
    FlashInfo,
    FlashActions,
    Modal,
    VButton
  },
  data() {
    return {
      flash: null,
      flashData: null,
      sfdp: null,
      toastMessage: "", // To hold the message for the toast,
      cancelOperationFlag: false,
      manualSizeSelection: 1024 * 1024,
      customSize: 1024 * 1024,
    };
  },
  props: {
    adapter: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.flash = new Flashdumper(this.adapter);
  },
  computed: {
    size() {
      if (!this.flash) return '';
      try {
        return formatMemorySize(this.flash.getCapacity() * 8);
      } catch {
        return 'size unknown';
      }
    },
    hasCapacity() {
      return !!this.flash && this.flash.hasCapacity && this.flash.hasCapacity();
    },
    capacitySource() {
      return this.flash && this.flash.getCapacitySource ? this.flash.getCapacitySource() : null;
    },
    detectedLabel() {
      if (!this.flash) return '';
      let vendor;
      if (this.flash.rdid) {
        vendor = this.flash.rdid.vendor
          || `Unknown (0x${this.flash.rdid.vendor_id.toString(16).toUpperCase()})`;
      } else {
        vendor = 'Unknown vendor';
      }
      return this.hasCapacity ? `${vendor} · ${this.size}` : vendor;
    },
    commonSizes() {
      // Values in KiB; most SPI flash capacities fit this grid.
      return [64, 128, 256, 512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072]
        .map(kib => ({
          value: kib * 1024,
          label: formatMemorySize(kib * 1024 * 8)
        }));
    }
  },

  methods: {
    showModal() {
      this.modal = new bootstrap.Modal(document.getElementById("flashModal"), {
        backdrop: "static",
        keyboard: false,
      });
      this.modal.show();
    },

    hideModal() {
      if(this.modal === undefined) {
        return;
      }
      clearInterval(this.updateElapsedTime);
      this.modal.hide();
      console.log("hideModal", this.modal);
      this.modal = undefined;
    },

    async waitForIdleSFDPRead() {
      if(this.cancelOperationFlag) {
        this.cancelOperationFlag = false;
        return;
      }
      console.log("Checking whether still busy")
      const pinState = await this.flash.adapter.pinState();
      if(this.cancelOperationFlag) {
        this.cancelOperationFlag = false;
        return;
      }
      if (pinState !== 1) {
        clearInterval(this.checkIdleInterval);
        this.hideModal();
        this.flash.readSFDP()
          .then((sfdp) => {
            this.sfdp = sfdp;
          })
          .catch((error) => {
            alert("Error: " + (error.message || "SFDP not found"));
            this.sfdp = null;
          });
      }
    },

    cancelOperation() {
      this.cancelOperationFlag = true;
      clearInterval(this.checkIdleInterval);
      this.hideModal();
    },

    // async checkIdle() {
    //   if(this.flash.adapter.supports_idle_check) {
    //     const isIdle = await this.flash.adapter.idleCheck()
    //     if(!isIdle) {
    //       this.showModal();
    //     } else {
    //       return true;
    //     }
    //   }
    // },

    async discover() {
      console.log("DISC")
      this.waitForIdle().then(async () => {
        try {
          await this.flash.identify();
          if (!this.flash.detected) {
            alert("No response from the flash: both SFDP and RDID returned empty/invalid data. Check the wiring and power, or use 'MANUAL SIZE' to proceed with defaults.");
          } else if (!this.hasCapacity) {
            // detected something (e.g. vendor-only RDID) but no usable size
            console.log("Detected chip but no capacity available — prompting for manual size");
          }
        } catch (exception) {
          alert(exception)
          console.log("Error during identify: " + exception)
        }
      });
    },

    showManualSize() {
      if (this.flash) {
        try {
          const cur = this.flash.getCapacity();
          const matching = this.commonSizes.find(s => s.value === cur);
          if (matching) {
            this.manualSizeSelection = cur;
          } else {
            this.manualSizeSelection = 'custom';
            this.customSize = cur;
          }
        } catch {
          // no current capacity — leave defaults
        }
      }
      this.$refs.manualSizeModal.show();
    },

    closeManualSize() {
      this.$refs.manualSizeModal.hide();
    },

    applyManualSize() {
      let size = this.manualSizeSelection;
      if (size === 'custom') {
        size = parseInt(this.customSize, 10);
      }
      if (!Number.isFinite(size) || size <= 0) {
        alert("Please enter a valid size in bytes.");
        return;
      }
      if (!this.flash) {
        this.flash = new Flashdumper(this.adapter);
      }
      this.flash.setManualCapacity(size);
      this.$refs.manualSizeModal.hide();
    },

    async discoverRDID() {
      this.waitForIdle().then(async () => {
        console.log("exec RDID");
        const rdid = await this.flash.readRDID();
        console.log("RDID", rdid);
        const vendor = findVendor(rdid)
        // TODO: Move to flashdumper.js
        this.flash.vendor = vendor
        this.flash.jedec_id = {}
        if(this.flash.jedec_id.vendor == null) {
          this.flash.jedec_id.vendor = "Unknown (0x" + rdid[1].toString(16) + ")";
        }
        this.flash.jedec_id.type = rdid[2];
        console.log("Jedec id:",this.flash.jedec_id)
        
      })
    },



    waitForIdle() {
      if(!this.flash.adapter.supports_idle_check) {
        return new Promise((resolve) => {
          resolve();
        })
      };
      // this.showModal();
      const promise = new Promise((resolve, reject) => {
        var check = () => {
          this.flash.adapter.pinState().then((pinState) => {
            // console.log("Idle check")
            if (pinState !== 1) {
              console.log("is idle")
              this.hideModal()
              resolve();
            } else {
              console.log("is not idle")
              this.showModal()
              setTimeout(() => {
                check();
              }, 1000);
            }
          })
        }
        check();
      });
      return promise;
    },


    //     const isIdle = await this.flash.adapter.idleCheck();
    //   if(isIdle) {
    //     clearInterval(this.checkIdleInterval);
    //     this.hideModal();
    //     this.flash.readSFDP()
    //       .then((sfdp) => {
    //         this.sfdp = sfdp;
    //       })
    //       .catch((error) => {
    //         alert("Error: " + (error.message || "SFDP not found"));
    //         this.sfdp = null;
    //       });
    //   }
    //   })
    // },

    async discoverSFDP() {
      if (this.flash.adapter.supports_idle_check) {
        const pinState = await this.flash.adapter.pinState()
        if (pinState === 1) {
          this.showModal()
          this.checkIdleInterval = setInterval(() => {
            this.waitForIdleSFDPRead();
          }, 500);
          return;
        }
      }

      await this.readSFDP();
    },

    async readSFDP() {
      await this.flash.winbond_reset()

      this.flash.readSFDP()
        .then((sfdp) => {
          this.sfdp = sfdp;
        })
        .catch((error) => {
          alert("Error: " + (error.message || "SFDP not found"));
          this.sfdp = null;
        });
    },

    showToast(message) {
      this.toastMessage = message;
      const toastElement = document.getElementById('toast');
      const toast = new bootstrap.Toast(toastElement, { delay: 3000 });
      toast.show();
    }
  }
};
</script>

<style scoped>
.component-headline {
  font-size: 1.2em;
}
</style>